import {
  FormData,
  VacationLookupEntry,
  UnifiedTableRow,
  YearlyTotals,
  AnnualMetrics,
  Assumptions,
  Assumption
} from "../Models/disability-calculator";
import {
  toNumber,
  ceil2,
  getYearValueOrDefault,
  getTFPRate,
  parseUTCDate,
  incrementToNextMonthStart, parsePercentage,
  debug,
  roundTo5Decimals
} from "./utils";
// Cache for unified sick-vacation tables
const unifiedTableCache = new Map<string, UnifiedTableRow[]>();

// Cache for unified annual tables
const unifiedAnnualTableCache = new Map<string, { [year: string]: AnnualMetrics }[]>();

// To calculate the Rounded
const calculateRounded = (value : number, multiplier : number, roundTo : number) => {
    return Math.round(value * multiplier / roundTo) * roundTo;
  }

// Generate cache key for unified table based on relevant formData
export function generateUnifiedTableCacheKey(formData: FormData): string {
  const { employmentInfo, performanceBenefits, financialInfo } = formData;

  const keyComponents = [
    employmentInfo.cafo || "",
    employmentInfo.dateOfBirth || "",
    employmentInfo.hireDate || "",
    employmentInfo.dateOfRetirement,
    employmentInfo.dateOfDisability,
    performanceBenefits.longevity || 0,
    performanceBenefits.rate || "",
    performanceBenefits.tfps,
    performanceBenefits.averageTfp,
    performanceBenefits.sickBankStart || 0,
    performanceBenefits.sickUseAfterEp || 0,
    financialInfo.preTax || "",
    financialInfo.roth || "",
    financialInfo.stateTaxBracket,
    financialInfo.weeksOfVacation || "",
    financialInfo.stdPlan || "",
    financialInfo.ltdPlan || "",
    financialInfo.lolTaxable || "",
  ];

  return keyComponents.join("|");
}


export function generateUnifiedSickVacationTable(formData: FormData): UnifiedTableRow[] {
  const cacheKey = generateUnifiedTableCacheKey(formData);

  if (unifiedTableCache.has(cacheKey)) {
    return unifiedTableCache.get(cacheKey)!;
  }

  const unifiedTable = generateUnifiedSickVacationTableInternal(formData);

  unifiedTableCache.set(cacheKey, unifiedTable);
  return unifiedTable;
}

function generateUnifiedSickVacationTableInternal(formData: FormData): UnifiedTableRow[] {
  const { employmentInfo, performanceBenefits } = formData;

  // Get dates
  const disabilityDate = employmentInfo.dateOfDisability;
  const retirementDate = employmentInfo.dateOfRetirement;

  if (!disabilityDate || !retirementDate) {
    return []; // Return empty table if dates missing
  }

  // Parse dates using UTC
  const disabilityDateObj = parseUTCDate(disabilityDate);
  const retirementDateObj = parseUTCDate(retirementDate);

  // Calculate elimination period end (60 days from disability date)
  const eliminationPeriodEndDate = new Date(Date.UTC(
    disabilityDateObj.getUTCFullYear(),
    disabilityDateObj.getUTCMonth(),
    disabilityDateObj.getUTCDate() + 60
  ));

  // Calculate which month (from disability) the elimination period ends in
  // Count actual months between disability date and elimination end date
  const disabilityMonth = disabilityDateObj.getUTCMonth();
  const disabilityYear = disabilityDateObj.getUTCFullYear();
  const eliminationEndMonth = eliminationPeriodEndDate.getUTCMonth();
  const eliminationEndYear = eliminationPeriodEndDate.getUTCFullYear();

  const eliminationPeriodLastMonth =
    (eliminationEndYear - disabilityYear) * 12 +
    (eliminationEndMonth - disabilityMonth) +
    1;

  // Calculate total vacation days from all Januarys between disability and retirement
  const totalJanuaryVacationDays = calculateTotalJanuaryVacationDays(
    disabilityDateObj,
    retirementDateObj,
    employmentInfo,
    formData.vacationRates
  );
  const totalVacaAcc = totalJanuaryVacationDays * 0.1;

  // Calculate sick use after EP for first post-EP month (will be used for all subsequent months)

  const disabilityUTC = disabilityDateObj;
  const retirementUTC = retirementDateObj;

  if (isNaN(disabilityUTC.getTime()) || isNaN(retirementUTC.getTime())) {
    return []; // Return empty table if invalid dates
  }

  const unifiedTable: UnifiedTableRow[] = [];
  let currentDate = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate()
  ));
  let monthFromDisability = 1;

  // Initialize sick bank tracking
  let currentSickBalStart = Number(performanceBenefits.sickBankStart) || 0;

  // Initialize vacation tracking
  // Calculate initial vacation balance dynamically based on longevity and disability date
  const initialVacEndBal = calculateInitialVacationBalance(formData);
  let currentVacEndBal = initialVacEndBal;

  let sickPayBecameZero = false;
  
  // Track first month with greatestBase > 0 for cash award logic
  let foundFirstGreatestBaseMonth = false;

  // Get calculation constants
  const tfpsInput = Number(performanceBenefits.tfps) || 0;
  const averageTfp = Number(performanceBenefits.averageTfp) || 0;

  // Calculate months to retirement for sick use calculation
  let monthsToRetirement = calculateMonthsToRetirement(
    employmentInfo.dateOfBirth || "",
    employmentInfo.dateOfDisability
  );

  // Calculate longevity (Years of Service) using the dedicated method
  const longevity = calculateLongevity(
    employmentInfo.hireDate || "",
    employmentInfo.dateOfDisability
  );


  // NEC
  const NEC = formData.assumptions.NECRates;


  while (currentDate < retirementUTC) {
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth(); // 0-11
    
    // Store the starting balance for this month (before any modifications)
    const monthStartingSickBalance = currentSickBalStart;
    const monthAbbr = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ][month];

    // Calculate LOL benefit for this month using original UI method with actual year
    const lolBenefit = calculateLOLTaxBenefit(
      formData,
      monthAbbr,
      monthFromDisability - 1,
      year
    );

    // Calculate Greatest Base from baseline table
    const greatestBase = calculateGreatestBase(lolBenefit, monthFromDisability);

    // Calculate PDE = TFPs × Average TFP (both from form inputs)
    const tfpsInputCurrent = Number(performanceBenefits.tfps) || 0;
    const PDE = tfpsInputCurrent * averageTfp;

    const necRate = getYearValueOrDefault(NEC, year.toString()); // month is 0-11
    
    // Calculate lolNecBase with simplified logic
    let lolNecBase = 0;
    
    if (greatestBase === 0) {
      // Elimination period - no cash award
      lolNecBase = 0;
    } else if (!foundFirstGreatestBaseMonth) {
      // First month with greatestBase > 0 - use existing formula
      lolNecBase = greatestBase * 2 * necRate;
      foundFirstGreatestBaseMonth = true; // Mark that we found the first month
    } else {
      // All subsequent months - use new formula
      lolNecBase = tfpsInputCurrent * averageTfp * necRate;
    }
    
    const necPde = ceil2(lolNecBase / necRate);

    // Calculate Sick & Able to Use
    const sickAndAbleToUse = PDE - greatestBase;

    // VACATION PROCESSING (determines sick bank accrual)

    // Determine if this is the first month (disability month)
    const isFirstMonth = monthFromDisability === 1;

    // Check if sick pay became zero (affects vacation credits)
    if (!sickPayBecameZero && currentSickBalStart <= 1) {
      sickPayBecameZero = true;
    }

    // Check if sick pay recovered (reset flag when balance > 1)
    if (sickPayBecameZero && currentSickBalStart > 1) {
      sickPayBecameZero = false;
    }

    // Vacation credits will be calculated after sick use is determined
    let totalVacaCred = 0.0; // Placeholder, will be set after sick use calculation

    let vacaUse = null;
    let accOnVaca = 0.0; // Vacation accrual to sick bank

    // January logic: use previous December's VacEndBal (but not in retirement month)
    const isRetirementMonth =
      year === retirementUTC.getUTCFullYear() &&
      month === retirementUTC.getUTCMonth();

    if ((month === 0 || isRetirementMonth) && !isFirstMonth) {
      // January payout OR retirement month - pay out accumulated vacation
      vacaUse = Math.round(currentVacEndBal * 100000) / 100000;
      accOnVaca = Math.round(vacaUse * 0.1 * 100) / 100; // 10% conversion rate
    }

    // Store vacation start balance for later calculation
    const vacaStartBal = currentVacEndBal;


    // SICK USE CALCULATION (uses total vacation accrual)

    let sickUse;
    if (monthFromDisability <= eliminationPeriodLastMonth) {
      // ELIMINATION PERIOD LOGIC
      const PDECurrent = averageTfp * tfpsInput; // User inputs
      const lolBenefitCurrent = calculateLOLTaxBenefit(
        formData,
        monthAbbr,
        monthFromDisability - 1,
        year
      );
      const greatestBaseCurrent = calculateGreatestBase(
        lolBenefitCurrent,
        monthFromDisability
      );
      const sickAndAbleToUseCurrent = PDECurrent - greatestBaseCurrent;
      const sickUseRaw = sickAndAbleToUseCurrent / tfpsInput;
      sickUse =
        Math.round(Math.min(sickUseRaw, currentSickBalStart) * 100000) / 100000;
    } else {
      // POST-ELIMINATION LOGIC - Use constant from user input
      if (currentSickBalStart > 1) {
        sickUse = Number(performanceBenefits.sickUseAfterEp) || 0;
      } else {
        // Balance depleted - no sick pay
        sickUse = 0;
      }
    }

    // Calculate Sic Bal End (rounded to 2 decimal places, cannot be negative)
    const sicBalEndRaw = currentSickBalStart - sickUse + accOnVaca;
    const sicBalEnd = Math.max(0, Math.round(sicBalEndRaw * 100000) / 100000);

    // Calculate monthly longevity using Excel DATEDIF formula equivalent
    const currentMonthDateForLongevity = new Date(Date.UTC(year, month, 1));
    const currentLongevity = calculateMonthlyLongevity(employmentInfo.hireDate || "", currentMonthDateForLongevity);

    // Calculate vacation credits based on sick use formula: Vaca Credits = 1 if Sick Use > 1, else 0
    let vacaCredits;

    vacaCredits = sickUse >= 1 ? 1 : 0; // Formula: 1 if sickUse > 1, else 0

    // Calculate Total Vaca Cred = Vaca Credits × multiplier using current longevity
    const credits = getDaysFromVacationTable(currentLongevity, formData.vacationRates);
    totalVacaCred = vacaCredits * credits;

    // Now recalculate vacation balance with correct vacation credits
    currentVacEndBal = Math.max(0, vacaStartBal + totalVacaCred - (vacaUse || 0));

    // Calculate Sick Pay (only if Sick Bal Start > 1 and not retirement month)
    let sickPay = 0;
    let dynamicTFPRate = 0;
    if (currentSickBalStart > 1 && !isRetirementMonth) {
      dynamicTFPRate = getDynamicTFPRateWithLongevity(formData, year, currentLongevity);
      sickPay = Math.round(sickUse * dynamicTFPRate * 100) / 100;
    } else {
      // Still get the dynamic TFP rate for display purposes even if no sick pay
      dynamicTFPRate = getDynamicTFPRateWithLongevity(formData, year, currentLongevity);
    }

    // STD CALCULATION (Short Term Disability)

    // Create a Date object for current month to use with STD calculation
    const currentMonthDate = new Date(Date.UTC(year, month, 1));

    // Calculate STD using existing method with year awareness
    // Check if STD plan is enabled
    const stdPlanEnabled = formData.financialInfo.stdPlan;
    const std =
      stdPlanEnabled === "Yes" || stdPlanEnabled === "yes"
        ? calculateSTDWithYear(formData, monthAbbr, year, currentMonthDate)
        : 0;

    // LTD CALCULATION (Long Term Disability)

    // Calculate LTD using existing method (handles Plan A vs Plan B)
    const ltdValue = calculateLTDA(
      formData,
      monthAbbr,
      monthFromDisability - 1,
      year
    );

    // Add row to unified table
    unifiedTable.push({
      // Sick bank data
      monthFromDisability: monthFromDisability,
      year: year,
      month: monthAbbr,
      monthAbbr: monthAbbr, // Add explicit monthAbbr field
      lolBenefit: Math.round(lolBenefit * 100) / 100,
      greatestBase: Math.round(greatestBase * 100) / 100,
      sickAndAbleToUse: Math.round(sickAndAbleToUse * 100) / 100,
      sickUse: Math.round(sickUse * 100) / 100,
      sickBalStart: monthStartingSickBalance,
      accOnVaca: Math.round(accOnVaca * 100) / 100, // SickAcc on Vaca
      sicBalEnd: Math.round(sicBalEnd * 100) / 100,
      sickPay: Math.round(sickPay * 100) / 100,

      // Vacation data
      vacaStartBal: vacaStartBal, // Vaca Start Bal
      totalVacaCred: totalVacaCred, // Total Vaca Cred
      vacaUse: vacaUse, // Vaca Use
      vacEndBal: currentVacEndBal, // Vac End Bal
      vacationPay: vacaUse
        ? Math.round(vacaUse * 3.75 * dynamicTFPRate * 100) / 100
        : 0, // Gross vacation pay for UI
      grossVac: vacaUse
        ? Math.round(vacaUse * 3.75 * dynamicTFPRate * 100) / 100
        : 0, // Gross Vaca logs
      lolNecBase: lolNecBase,
      necRate: necRate,
      necPde: necPde,

      // STD and LTD data
      std: Math.round(std * 100) / 100,
      ltdValue: Math.round(ltdValue * 100) / 100,

      // Combined STD + LTD total
      totalltdstd: Math.round((std + ltdValue) * 100) / 100,

      // Longevity tracking columns
      currentLongevity: currentLongevity,
      dynamicTFPRate: Math.round(dynamicTFPRate * 100) / 100,
    });


   // Calculate remaining months to retirement for next iteration
    const remainingMonthsToRetirement = monthsToRetirement - monthFromDisability;
    
    // Set up for next month with retirement-aware sick balance handling
    if (remainingMonthsToRetirement > 0) {
      // Continue with normal sick balance progression
      currentSickBalStart = sicBalEnd;
    } else {
      // Final month before retirement - reset sick balance to zero
      currentSickBalStart = 0;
    }

    monthFromDisability++;
    currentDate = incrementToNextMonthStart(currentDate);

    debug(`${monthAbbr}-${year} calculations:
      year: ${year}
      month: ${monthAbbr}
      currentLongevity: ${currentLongevity}
      dynamicTFPRate: ${dynamicTFPRate}
      lolBenefit: ${lolBenefit}
      greatestBase: ${greatestBase}
      sickAndAbleToUse: ${sickAndAbleToUse}
      sickUse: ${sickUse}
      sickBalStart: ${monthStartingSickBalance} (rounded: ${Math.round(monthStartingSickBalance * 100) / 100})
      accOnVaca: ${accOnVaca}
      sicBalEnd: ${sicBalEnd}
      sickPay: ${sickPay}
      vacaStartBal: ${vacaStartBal}
      totalVacaCred: ${totalVacaCred}
      vacaUse: ${vacaUse}
      vacEndBal: ${currentVacEndBal}
      vacationPay: ${vacaUse ? Math.round(vacaUse * 3.75 * dynamicTFPRate * 100) / 100 : 0}
      grossVac: ${vacaUse ? Math.round(vacaUse * 3.75 * dynamicTFPRate * 100) / 100 : 0}
      lolNecBase: ${lolNecBase}
      necRate: ${necRate}
      necPde: ${necPde}
      std: ${std}
      ltdValue: ${ltdValue}
      totalltdstd: ${std + ltdValue}
    `);
  }

  return unifiedTable;
}

export function generateUnifiedAnnualTable(formData: FormData): { [year: string]: AnnualMetrics }[] {
  debug('Starting generateUnifiedAnnualTable');

  
  let ASSUMPTIONS : Assumptions = formData.assumptions;
  
  const cacheKey = generateUnifiedTableCacheKey(formData);
  
  if (unifiedAnnualTableCache.has(cacheKey)) {
    debug('Returning cached result');
    return unifiedAnnualTableCache.get(cacheKey)!;
  }
  
  // Helper Methods
  const INT = (n: string | number | null | undefined) =>
    Math.round(toNumber(n));
  
  const ensureAssumption = (year: string, calcType: string) => {
    const current = ASSUMPTIONS.AssumptionByYear[year];
    if (current[calcType] === null || current[calcType] === undefined) {
      const val = calculateAssumption(year, ASSUMPTIONS.AssumptionByYear, calcType);
      ASSUMPTIONS.AssumptionByYear[year] = {
        ...ASSUMPTIONS.AssumptionByYear[year],
        [calcType] : String(val)
      }
      return val;
    }
  return toNumber(current[calcType]);

  }
  const annualTable: { [year: string]: AnnualMetrics }[] = [];
  /**
   * Calculation for Income Replacement - Annually
   */
  const { employmentInfo, financialInfo } = formData;

  const disabilityDate = employmentInfo.dateOfDisability;
  const retirementDate = employmentInfo.dateOfRetirement;
  if (!disabilityDate || !retirementDate) return [];

  const startYear0 = parseUTCDate(disabilityDate).getFullYear();
  const endYear = parseUTCDate(retirementDate).getFullYear();

  // Source tables
  const unifiedTable = generateUnifiedSickVacationTable(formData);
  const yearlyTotals = calculateYearlyTotals(unifiedTable);

  const dobString = employmentInfo.dateOfBirth; // "MM/DD/YYYY"
  const birthYear = parseInt(String(dobString).split("/")[2], 10);

  // NEC and MBCBP
  const NEC = formData.assumptions.NECRates;
  const MBC_BP = formData.assumptions.MBCBPRates;

  let index = 0;

  // Assumption Initialization
  let _401a17 = 0;
  let _SSWage = 0;
  let valueOf415cNec = 0;
  let valueOf414v = 0;
  let valueOf402g = 0;
  let valueOfSuper = 0;
  let _401a17Cpy: string[] = Object.values(ASSUMPTIONS.AssumptionByYear).map(item => String(item["_401a17"]));

  for (let y = startYear0; y <= endYear; y++) {
    debug(`Processing year: ${y}`);

    const year = String(y);

    // Assumptions 
    let currentYearAssumption: Assumption | {} = ASSUMPTIONS.AssumptionByYear[year] || {};

    if(currentYearAssumption["_401a17"] === undefined || currentYearAssumption["_401a17"] === null)
    {
      if(_401a17Cpy.length < 10 && index > 10)
        throw new Error("Cannot proceed: insufficient 401a17 history.");

        
      let result = _401a17 * (1 + (Math.pow(_401a17 / toNumber(ASSUMPTIONS.AssumptionByYear[(y-11)]["_401a17"]), 1 / 10) - 1));
      _401a17 = isNaN(result) ? 0 : calculateRounded(result,1,1000) 
      ASSUMPTIONS.AssumptionByYear[year] = {
        ...ASSUMPTIONS.AssumptionByYear[year],
        _401a17 : String(_401a17)
      };
      _401a17Cpy.push(_401a17.toString())
    }
    else
      _401a17 = toNumber(currentYearAssumption["_401a17"])

    valueOf415cNec = ensureAssumption(year, "_415c")
    _SSWage = ensureAssumption(year, "SS_Wage");
    valueOf414v = ensureAssumption(year, "_414v");
    valueOf402g = ensureAssumption(year, "_402g");
    valueOfSuper = ensureAssumption(year, "SUPER");



    const sums = yearlyTotals[year] || {
      greatestBaseSum: 0,
      sickPaySum: 0,
      grossVacSum: 0,
      necPdeSum: 0,
      totalltdstdSum: 0,
    };

    // rates (as decimals, e.g., 0.17)
    const necPct = getYearValueOrDefault(NEC, year);
    const mbcBpPct = getYearValueOrDefault(MBC_BP, year);

    // Round to 5 decimal places with proper floating-point precision handling
    const pbeMbe = roundTo5Decimals(sums.necPdeSum);
    const lolBase = roundTo5Decimals(sums.greatestBaseSum);
    const taxableSickPay = roundTo5Decimals(sums.sickPaySum);
    const taxableVacationPay = roundTo5Decimals(sums.grossVacSum);

    // LOL flags
    const lolTaxableFlag = String(financialInfo.lolTaxable || "").toLowerCase();
    const taxableLOL = lolTaxableFlag === "taxable" ? lolBase : 0; // integer
    const nonTaxableLOL = taxableLOL === 0 ? lolBase : 0; // integer

    // Cash award (NEC on PBE/MBE when LOL not taxable)
    const taxableCashAward = taxableLOL === 0 ? roundTo5Decimals(pbeMbe * necPct) : 0;

    // Deferrable wage (401k base): LOL (if taxable) + sick + vacation
    const deferrableWage = roundTo5Decimals(
      taxableLOL + taxableSickPay + taxableVacationPay
    );

    // EE 401(k) pre-tax (cap at 402g/414v/SUPER)
    const preTaxPct = parsePercentage(financialInfo.preTax); // e.g., 0.15
    const rothTaxPct = parsePercentage(financialInfo.roth); // e.g., 0.25

    const totatTaxPct = preTaxPct + rothTaxPct;

    const currentAge = y - birthYear;
    const SUPER =
      currentAge >= 60 && currentAge <= 63
        ? valueOfSuper - valueOf414v
        : 0;
    const value402g414v = roundTo5Decimals(
      (currentAge >= 50
        ? valueOf402g + valueOf414v
        : valueOf402g) + (SUPER > 1 ? SUPER : 0)
    );

    
    // EE 401(k) Sub
    const ee401kSub = roundTo5Decimals(deferrableWage * (totatTaxPct));
            
    const ee401kPTAX = -roundTo5Decimals(ee401kSub * preTaxPct)// negative integer

    // Max Roth Def
    const maxRothDef = roundTo5Decimals(value402g414v + SUPER);

    const ee401kTotal = -Math.min(maxRothDef, roundTo5Decimals(ee401kSub * totatTaxPct));

    const ee401kRoth = roundTo5Decimals(ee401kTotal - ee401kPTAX);

    /**
     * NEC – Annually
     */
    const mbeNec = taxableCashAward > 0 ? 0 : roundTo5Decimals(sums.necPdeSum);
    const otherWagesNec = roundTo5Decimals(sums.sickPaySum + sums.grossVacSum);
    const totalWageNec = roundTo5Decimals(mbeNec + otherWagesNec);

    const over401aNec = Math.max(0, totalWageNec - _401a17);
    const valueOf401a17Nec = roundTo5Decimals(over401aNec * necPct);

    // ER NEC (minus 401a17 overage); if cash award exists, only on other wages
    const totalER =
      taxableCashAward === 0
        ? roundTo5Decimals((mbeNec + otherWagesNec) * necPct) - valueOf401a17Nec
        : roundTo5Decimals(otherWagesNec * necPct);

    // EE NEC (to 402g) — cap at 402g
    const eeNec = Math.min(-ee401kTotal, valueOf402g); // positive integer (cap)
    const EEERNec = roundTo5Decimals(totalER + eeNec);

    
    const excessNec = Math.max(0, EEERNec - valueOf415cNec); // integer

    // Calculate Qual 
    const qual = Math.min(EEERNec, valueOf415cNec);

    // Calculate 401kQual+Catch
    const qual401kCatch = roundTo5Decimals(-ee401kTotal - eeNec + qual);

    /**
     * MBC/BP – Annually
     */
    const mbeMbcbp = sums.necPdeSum;
    const otherWagesMbcbp =   roundTo5Decimals(sums.sickPaySum + sums.grossVacSum)

    const totalWageMBCBP = roundTo5Decimals(
      mbeMbcbp + otherWagesMbcbp
    );

    const totalDirectMbcbp = roundTo5Decimals(
      totalWageMBCBP * mbcBpPct
    )

    // Calculate Total Qualified Retirement: Total Direct + 401kQual+Catch
    const totalQualifiedRetirement = roundTo5Decimals(totalDirectMbcbp + qual401kCatch);

    const over401aMbcBp = Math.max(0, totalWageMBCBP - _401a17);
    const greaterValueOf401a17Mb = roundTo5Decimals(over401aMbcBp * mbcBpPct);

    /**
     * Income Replacement – Annually
     */
    const taxableOverage =
      taxableCashAward > 0 ? 0 : roundTo5Decimals(valueOf401a17Nec + greaterValueOf401a17Mb);
    const taxableExcess = roundTo5Decimals(excessNec);

    const pilotContributions = roundTo5Decimals(ee401kPTAX + ee401kRoth);

    annualTable.push({
      [year]: {
        lolBase,
        taxableSickPay,
        taxableVacationPay,
        taxableLOL,
        nonTaxableLOL,
        deferrableWage,
        pilotContributions,
        ee401kPTAX,
        ee401kTotal,
        taxableOverage,
        taxableExcess,
        taxableCashAward,
        mbeNec,
        otherWagesNec,
        mbeMbcbp,
        otherWagesMbcbp,
        totalDirectMbcbp,
        totalltdstd: INT(sums.totalltdstdSum),
        ssWage: _SSWage,
        qual401kCatch,
        totalQualifiedRetirement
      },
    });

    debug(`Year ${year} calculations:
      year: ${year}
      lolBase: ${lolBase}
      taxableSickPay: ${taxableSickPay}
      taxableVacationPay: ${taxableVacationPay}
      taxableLOL: ${taxableLOL}
      nonTaxableLOL: ${nonTaxableLOL}
      deferrableWage: ${deferrableWage}
      ee401kPTAX: ${ee401kPTAX}
      ee401kTotal: ${ee401kTotal}
      ee401kSub: ${ee401kSub}
      maxRothDef: ${maxRothDef}
      taxableOverage: ${taxableOverage}
      taxableExcess: ${taxableExcess}
      taxableCashAward: ${taxableCashAward}
      totalltdstd: ${INT(sums.totalltdstdSum)}
      ssWage: ${_SSWage}
      _401a17: ${_401a17}
      valueOf415cNec: ${valueOf415cNec}
      valueOf402g: ${valueOf402g}
      valueOf414v: ${valueOf414v}
      valueOfSuper: ${valueOfSuper}
      necPct: ${necPct}
      mbcBpPct: ${mbcBpPct}
      pbeMbe: ${pbeMbe}
      mbeNec: ${mbeNec}
      otherWagesNec: ${otherWagesNec}
      totalWageNec: ${totalWageNec}
      over401aNec: ${over401aNec}
      valueOf401a17Nec: ${valueOf401a17Nec}
      totalER: ${totalER}
      eeNec: ${eeNec}
      EEERNec: ${EEERNec}
      excessNec: ${excessNec}
      qual: ${qual}
      qual401kCatch: ${qual401kCatch}
      totalQualifiedRetirement: ${totalQualifiedRetirement}
      totalWageMBCBP: ${totalWageMBCBP}
      over401aMbcBp: ${over401aMbcBp}
      greaterValueOf401a17Mb: ${greaterValueOf401a17Mb}
      value402g414v: ${value402g414v}
      SUPER: ${SUPER}
      currentAge: ${currentAge}

    `);
    index += 1;

  }

  unifiedAnnualTableCache.set(cacheKey, annualTable);
  return annualTable;
}

// Clear cache function
export function clearUnifiedTableCaches(): void {
  unifiedTableCache.clear();
  unifiedAnnualTableCache.clear();
}

function calculateInitialVacationBalance(formData: FormData): number {
  const {vacationRates, employmentInfo } = formData;
    const disabilityDate = parseUTCDate(employmentInfo.dateOfDisability);
    
    // Calculate longevity (Years of Service) using the dedicated method
    const longevity = calculateLongevity(employmentInfo.hireDate, employmentInfo.dateOfDisability);


    //Get permonthValue from the vacation lookup table (Days divided by 12)
    const perMonthValue = getDaysFromVacationTable(longevity,vacationRates)

   
    // Calculate initial balance: PerMonth × MONTH(Disability Date)
    const disabilityMonth = disabilityDate.getUTCMonth(); // 1-12
    
    const initialBalance = perMonthValue * disabilityMonth;

    
    const roundedBalance = Math.round(initialBalance * 100000) / 100000; 

    
    return roundedBalance;
}

function calculateLOLTaxBenefit(
  formData: FormData,
  month: string,
  monthIndex: number,
  actualYear: number | null = null
): number {
  // Extract relevant form data
  const { employmentInfo, performanceBenefits } = formData;

  // Get disability date from form
  const disabilityDate = employmentInfo.dateOfDisability;
  const retirementDate = employmentInfo.dateOfRetirement;

  if (!disabilityDate || !retirementDate) {
    return 0; // No payment if dates are missing
  }

  // Parse dates using UTC to avoid timezone issues
  const disabilityUTC = parseUTCDate(disabilityDate);
  const retirementUTC = parseUTCDate(retirementDate);

  // Validate parsed dates
  if (isNaN(disabilityUTC.getTime()) || isNaN(retirementUTC.getTime())) {
    return 0; // Invalid dates
  }

  // Calculate PDE = averageTfp * TFPs (using original TFPs from form input)
  const averageTfp = Number(performanceBenefits.averageTfp) || 0;
  const tfps = Number(performanceBenefits.tfps) || 0;
  const PDE = averageTfp * tfps;

  // Get current month date for calculations
  const currentMonthDate = getMonthDateForCalculationUTC(
    month,
    disabilityUTC,
    actualYear
  );

  // Check if current month is after retirement
  if (currentMonthDate >= retirementUTC) {
    return 0; // No payment after retirement
  }

  // Calculate first LOL payment date (Day 61 - same as LTD)
  const firstLOLDate = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 60
  )); // Day 61

  // Get month boundaries using UTC
  const monthStart = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), 1)
  );
  const monthEnd = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth() + 1, 0)
  );

  // Check if this month has any LOL payment days
  if (monthEnd < firstLOLDate) {
    return 0; // No LOL payment before Day 61
  }

  // Get chronological month number for 5% increase logic
  const chronologicalMonthNumber = getChronologicalMonthNumberUTC(
    disabilityUTC,
    currentMonthDate
  );

  // Determine base benefit amount (with 5% increase after month 60)
  let baseAmount: number;
  if (chronologicalMonthNumber <= 60) {
    baseAmount = PDE / 2; // PDE/2 for first 60 months
  } else {
    baseAmount = (PDE / 2) * 1.05; // 5% increase after month 60
  }

  // If LOL starts mid-month, calculate prorated payment
  if (firstLOLDate >= monthStart && firstLOLDate <= monthEnd) {
    const totalDaysInMonth = monthEnd.getUTCDate();
    const proratedDays = totalDaysInMonth - firstLOLDate.getUTCDate() + 1;
    const proratedAmount = (proratedDays / totalDaysInMonth) * baseAmount;
    return Math.round(proratedAmount * 100) / 100;
  } else {
    // Full month payment
    return Math.round(baseAmount * 100) / 100;
  }
}
function getMonthDateForCalculationUTC(
  monthAbbr: string,
  disabilityDate: Date,
  actualYear: number | null = null
): Date {
  const monthMap = {
    JUL: 6,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DEC: 11,
    JAN: 0,
    FEB: 1,
    MAR: 2,
    APR: 3,
    MAY: 4,
    JUN: 5,
  };

  const targetMonth = monthMap[monthAbbr];

  // If actualYear is provided (from sick bank table context), use it directly
  if (actualYear !== null) {
    return new Date(Date.UTC(actualYear, targetMonth, 1));
  }

  // Fallback to original logic for backwards compatibility
  const disabilityMonth = disabilityDate.getUTCMonth();
  const disabilityYear = disabilityDate.getUTCFullYear();

  // If target month is before disability month, it's next year
  let targetYear = disabilityYear;
  if (targetMonth < disabilityMonth) {
    targetYear = disabilityYear + 1;
  }

  return new Date(Date.UTC(targetYear, targetMonth, 1));
}

function getChronologicalMonthNumberUTC(disabilityDate: Date, currentMonthDate: Date): number {
  const startMonth = new Date(
    Date.UTC(disabilityDate.getUTCFullYear(), disabilityDate.getUTCMonth(), 1)
  );
  const currentMonth = new Date(
    Date.UTC(
      currentMonthDate.getUTCFullYear(),
      currentMonthDate.getUTCMonth(),
      1
    )
  );

  const monthsDiff =
    (currentMonth.getUTCFullYear() - startMonth.getUTCFullYear()) * 12 +
    (currentMonth.getUTCMonth() - startMonth.getUTCMonth()) +
    1;

  return monthsDiff;
}

function calculateGreatestBase(
  lolBenefit: number,
  monthFromDisability: number
): number {
  const lolBaseGross = lolBenefit;
  const base5YR = monthFromDisability > 60 ? lolBenefit * 1.05 : 0;
  const base10YR = monthFromDisability > 120 ? base5YR * 1.05 : 0;
  const base15YR = monthFromDisability > 180 ? base10YR * 1.05 : 0;
  const base20YR = monthFromDisability > 240 ? base15YR * 1.05 : 0;
  const base25YR = monthFromDisability > 300 ? base20YR * 1.05 : 0;

  return Math.max(
    lolBaseGross,
    base5YR,
    base10YR,
    base15YR,
    base20YR,
    base25YR
  );
}


function getDynamicTFPRate(formData: FormData, year: number): number {
  const { employmentInfo, performanceBenefits, tfpRates } = formData;
  
  // Get base information
  const disabilityDate = employmentInfo.dateOfDisability;
  const hireDate = employmentInfo.hireDate;
  const seat = employmentInfo.cafo; // CA or FO
  
  if (!disabilityDate || !hireDate || !seat) {
    return Number(performanceBenefits.averageTfp) || 0;
  }

  // Calculate base longevity at disability date
  const baseLongevity = calculateLongevity(hireDate, disabilityDate);
  
  // Calculate years elapsed since disability
  const disabilityYear = parseUTCDate(disabilityDate).getUTCFullYear();
  const yearsElapsed = year - disabilityYear;
  
  // Calculate current longevity for this year
  const currentLongevity = baseLongevity + yearsElapsed;
  
  // Cap longevity at 12 (highest rate available in API)
  const cappedLongevity = Math.min(Math.max(currentLongevity, 1), 12);
  
  // Build dynamic rate code (e.g., CA3, FO5)
  const dynamicRateCode = `${seat}${cappedLongevity}`;
  
  // Get TFP rate using dynamic rate code and year
  const tfpRate = getTFPRate(dynamicRateCode, year, tfpRates);
  
  return tfpRate;
}

// New function that uses pre-calculated monthly longevity
function getDynamicTFPRateWithLongevity(formData: FormData, year: number, longevity: number): number {
  const { employmentInfo, performanceBenefits, tfpRates } = formData;
  
  // Get seat position
  const seat = employmentInfo.cafo; // CA or FO
  
  if (!seat) {
    return Number(performanceBenefits.averageTfp) || 0;
  }
  
  // Cap longevity at 12 (highest rate available in API)
  const cappedLongevity = Math.min(Math.max(longevity, 1), 12);
  
  // Build dynamic rate code (e.g., CA3, FO5)
  const dynamicRateCode = `${seat}${cappedLongevity}`;
  
  // Get TFP rate using dynamic rate code and year
  const tfpRate = getTFPRate(dynamicRateCode, year, tfpRates);
  
  return tfpRate;
}

function calculateSTDWithYear(formData: FormData, month: string, actualYear: number, monthDate: Date): number {
  const { employmentInfo, performanceBenefits, financialInfo } = formData;

  // Check if STD plan is enabled
  if (financialInfo.stdPlan !== "Yes" && financialInfo.stdPlan !== "yes") {
    return 0; // No STD payment if plan is not enabled
  }

  // Get disability date from form
  const disabilityDate = employmentInfo.dateOfDisability;

  if (!disabilityDate) {
    return 0; // No payment if dates are missing
  }

  // Parse disability date using UTC
  const disabilityUTC = parseUTCDate(disabilityDate);

  if (isNaN(disabilityUTC.getTime())) {
    return 0; // Invalid date
  }

  // Calculate STD payment period: Day 15 to Day 60 from disability (matching original calculateSTD)
  const firstSTDDate = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 14
  )); // Day 15 (start of STD)

  const lastSTDDate = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 59
  )); // Day 60 (end of STD)

  // Convert monthDate to UTC for comparison
  const monthDateUTC = new Date(
    Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth(), monthDate.getUTCDate())
  );
  const monthStart = new Date(Date.UTC(actualYear, monthDate.getUTCMonth(), 1));
  const monthEnd = new Date(Date.UTC(actualYear, monthDate.getUTCMonth() + 1, 0)); // Last day of month

  // Check if this month has any overlap with STD payment period
  if (monthEnd < firstSTDDate || monthStart > lastSTDDate) {
    // No STD payment for this month
    return 0;
  }

  // If within STD period, calculate the payment using existing logic
  // Get dynamic TFP rate - use the actual year of the month being calculated
  const TFPs = getDynamicTFPRate(formData, actualYear);

  const averageTfp = Number(performanceBenefits.averageTfp) || 0;

  const wage = averageTfp * TFPs * 12;
  const weeklyPDE = Math.round(wage / 52);
  const pde50Percent = weeklyPDE * 0.5;
  const benefit = Math.min(2500, pde50Percent);
  const dailyValue = benefit / 7;

  // Calculate applicable days for this specific month within STD period
  const periodStart = monthStart > firstSTDDate ? monthStart : firstSTDDate;
  const periodEnd = monthEnd < lastSTDDate ? monthEnd : lastSTDDate;

  // Calculate the number of days (inclusive)
  const timeDiff = periodEnd.getTime() - periodStart.getTime();
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
  const applicableDays = Math.max(0, daysDiff);

  const std = dailyValue * applicableDays;

  return Math.round(std * 100) / 100;
}

function calculateLTDA(formData: FormData, month: string, monthIndex: number, actualYear: number | null = null): number {
  const { employmentInfo, performanceBenefits, financialInfo } = formData;

  // Handle different LTD plans
  if (financialInfo.ltdPlan === "No") {
    return 0; // No LTD payment when "No" is selected
  } else if (financialInfo.ltdPlan === "Plan B") {
    return calculateLTDB(formData, month, monthIndex, actualYear);
  } else if (financialInfo.ltdPlan !== "Plan A") {
    return 0; // No LTD payment if plan not recognized
  }

  // Get disability date from form
  const disabilityDate = employmentInfo.dateOfDisability;

  if (!disabilityDate) {
    return 0; // No payment if dates are missing
  }

  // Parse disability date using UTC
  const disabilityUTC = parseUTCDate(disabilityDate);

  if (isNaN(disabilityUTC.getTime())) {
    return 0; // Invalid date
  }

  // Check if current month has LTD payment days (from 61st day onwards)
  const ltdApplicableDays = getLTDApplicableDays(
    disabilityUTC,
    month,
    actualYear
  );

  if (ltdApplicableDays === 0) {
    return 0; // No LTD A payment before Day 61
  }

  // Calculate PDE = averageTfp * TFPs (where TFPs is dynamic rate from lookup)
  const currentMonthDate = getMonthDateForCalculationUTC(
    month,
    disabilityUTC,
    actualYear
  );
  const currentYear = currentMonthDate.getUTCFullYear();
  const TFPs = getDynamicTFPRate(formData, currentYear); // TFPs is the dynamic rate
  const averageTfp = Number(performanceBenefits.averageTfp) || 0;
  const PDE = averageTfp * TFPs;

  // Calculate base LTD A = MIN(2500, PDE * 0.1)
  const baseLtdAmount = Math.min(2500, PDE * 0.1);

  // Calculate first LTD payment date (Day 61)
  const firstLTDDate = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 60
  )); // Day 61

  // Get month boundaries using UTC
  const monthStart = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), 1)
  );
  const monthEnd = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth() + 1, 0)
  );

  let multiplier = 1.0; // Default for full months

  // Check if this is the first month LTD starts (prorated calculation)
  if (firstLTDDate >= monthStart && firstLTDDate <= monthEnd) {
    // First month - calculate prorated multiplier
    const totalDaysInMonth = monthEnd.getUTCDate();
    const proratedDays = totalDaysInMonth - firstLTDDate.getUTCDate() + 1;
    multiplier = proratedDays / totalDaysInMonth;
  } else if (monthStart > firstLTDDate) {
    // Subsequent months - check if previous month had >= 1 prorated days
    const previousMonth = new Date(currentMonthDate);
    previousMonth.setUTCMonth(previousMonth.getUTCMonth() - 1);
    
    const previousMonthStart = new Date(
      Date.UTC(previousMonth.getUTCFullYear(), previousMonth.getUTCMonth(), 1)
    );
    const previousMonthEnd = new Date(
      Date.UTC(previousMonth.getUTCFullYear(), previousMonth.getUTCMonth() + 1, 0)
    );
    
    let previousMonthProratedDays = 0;
    
    // Check if LTD started in the previous month (first LTD month)
    if (firstLTDDate >= previousMonthStart && firstLTDDate <= previousMonthEnd) {
      // Previous month was the first LTD month - calculate prorated days
      previousMonthProratedDays = previousMonthEnd.getUTCDate() - firstLTDDate.getUTCDate() + 1;
    } else if (firstLTDDate < previousMonthStart) {
      // LTD started before previous month, so previous month had full days
      previousMonthProratedDays = previousMonthEnd.getUTCDate(); // Full month days
    }
    
    // Multiplier is 1.0 only if previous month prorated days >= 1
    multiplier = previousMonthProratedDays >= 1 ? 1.0 : 0;
  } else {
    // Before LTD starts - no payment
    multiplier = 0;
  }
  
  const finalLtdAmount = baseLtdAmount * multiplier;

  return Math.round(finalLtdAmount * 100) / 100; // Round to 2 decimal places
}

function calculateLTDB(formData: FormData, month: string, monthIndex: number, actualYear: number | null = null): number {
  const { employmentInfo, performanceBenefits } = formData;

  // Get disability date from form
  const disabilityDate = employmentInfo.dateOfDisability;

  if (!disabilityDate) {
    return 0; // No payment if dates are missing
  }

  // Parse disability date using UTC
  const disabilityUTC = parseUTCDate(disabilityDate);

  if (isNaN(disabilityUTC.getTime())) {
    return 0; // Invalid date
  }

  // Calculate key dates for dual-phase LTD B
  const firstLTDBPhase1Date = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 60
  )); // Day 61 - LTD B Phase 1 starts (follows LTD A rules)

  const firstLTDBPhase2Date = new Date(Date.UTC(
    disabilityUTC.getUTCFullYear(),
    disabilityUTC.getUTCMonth(),
    disabilityUTC.getUTCDate() + 180
  )); // Day 181 - LTD B Phase 2 starts (follows LTD B rules)

  // Get the month date for calculation
  const currentMonthDate = getMonthDateForCalculationUTC(
    month,
    disabilityUTC,
    actualYear
  );

  // Get month boundaries using UTC
  const monthStart = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), 1)
  );
  const monthEnd = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth() + 1, 0)
  );

  // Check if this month has any LTD B payment days
  if (monthEnd < firstLTDBPhase1Date) {
    return 0; // No LTD B payment days in this month (before Day 61)
  }

  // Determine which phase applies to this month
  const currentYear = currentMonthDate.getUTCFullYear();

  // Phase 1: Day 61-180 (follows LTD A rules)
  if (monthEnd < firstLTDBPhase2Date) {
    // Calculate PDE using dynamic TFP rates (like LTD A)
    const TFPs = getDynamicTFPRate(formData, currentYear);
    const averageTfp = Number(performanceBenefits.averageTfp) || 0;
    const PDE = averageTfp * TFPs;

    // Calculate LTD B Phase 1 = MIN(2500, PDE * 0.1) - same as LTD A
    const baseLtdAmount = Math.min(2500, PDE * 0.1);

    // Handle prorated payment for Phase 1 start
    if (firstLTDBPhase1Date >= monthStart && firstLTDBPhase1Date <= monthEnd) {
      // First month of Phase 1 - calculate prorated days
      const totalDaysInMonth = monthEnd.getUTCDate();
      const proratedDays = totalDaysInMonth - firstLTDBPhase1Date.getUTCDate() + 1;
      const proratedAmount = (proratedDays / totalDaysInMonth) * baseLtdAmount;
      return Math.round(proratedAmount * 100) / 100;
    } else {
      // Full month Phase 1 payment
      return Math.round(baseLtdAmount * 100) / 100;
    }
  }

  // Phase 2: Day 181+ (follows LTD B rules)
  else {
    // Calculate PDE using static TFP rates (original LTD B logic)
    const averageTfp = Number(performanceBenefits.averageTfp) || 0;
    const tfps = Number(performanceBenefits.tfps) || 0;
    const PDE = averageTfp * tfps;

    // Calculate LTD B Phase 2 = MIN(PDE * 15%, 6300)
    const ltdbNet = Math.min(PDE * 0.15, 6300);

    // Handle prorated payment for Phase 2 start
    if (firstLTDBPhase2Date >= monthStart && firstLTDBPhase2Date <= monthEnd) {
      // Transition month - need both LTD A and LTD B portions
      const totalDaysInMonth = monthEnd.getUTCDate();
      const phase2StartDay = firstLTDBPhase2Date.getUTCDate();
      
      // LTD A portion: Days 1 to phase2StartDay-1
      const ltdADays = phase2StartDay - 1;
      const TFPsForLTDA = getDynamicTFPRate(formData, currentYear);
      const PDEForLTDA = averageTfp * TFPsForLTDA;
      const baseLtdAAmount = Math.min(2500, PDEForLTDA * 0.1);
      const ltdAPortion = (ltdADays / totalDaysInMonth) * baseLtdAAmount;
      
      // LTD B portion: Days phase2StartDay to end of month
      const ltdBDays = totalDaysInMonth - phase2StartDay + 1;
      const ltdBPortion = (ltdBDays / totalDaysInMonth) * ltdbNet;
      
      // Combined amount for transition month
      const combinedAmount = ltdAPortion + ltdBPortion;
      return Math.round(combinedAmount * 100) / 100;
    } else {
      // Full month Phase 2 payment
      return Math.round(ltdbNet * 100) / 100;
    }
  }
}

function getLTDApplicableDays(disabilityDate: Date, month: string, actualYear: number | null = null): number {
  // LTD starts from 61st day onwards

  // Calculate first LTD payment date (Day 61)
  const firstLTDDate = new Date(Date.UTC(
    disabilityDate.getUTCFullYear(),
    disabilityDate.getUTCMonth(),
    disabilityDate.getUTCDate() + 60
  )); // Day 61

  // Get the month date for calculation
  const currentMonthDate = getMonthDateForCalculationUTC(
    month,
    disabilityDate,
    actualYear
  );

  // Get month boundaries using UTC
  const monthStart = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), 1)
  );
  const monthEnd = new Date(
    Date.UTC(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth() + 1, 0)
  );

  // Check if this month has any LTD payment days
  if (monthEnd < firstLTDDate) {
    return 0; // No LTD payment days in this month (before Day 61)
  }

  // If first LTD date falls within or before this month, LTD applies
  return firstLTDDate <= monthEnd ? 1 : 0;
}


function calculateYearlyTotals(unifiedTableData: UnifiedTableRow[]): YearlyTotals {
  return unifiedTableData.reduce((acc, item) => {
    const year = item.year;

    if (!acc[year]) {
      acc[year] = {
        greatestBaseSum: 0,
        sickPaySum: 0,
        grossVacSum: 0,
        necPdeSum: 0,
        totalltdstdSum: 0,
      };
    }

    acc[year].greatestBaseSum += roundTo5Decimals(toNumber(item.greatestBase));
    acc[year].sickPaySum += roundTo5Decimals(toNumber(item.sickPay));
    acc[year].grossVacSum += roundTo5Decimals(toNumber(item.grossVac));
    acc[year].necPdeSum += roundTo5Decimals(toNumber(item.necPde));
    acc[year].totalltdstdSum += roundTo5Decimals(toNumber(item.totalltdstd));

    return acc;
  }, {});
}

// Supporting function implementations

function calculateLongevity(hireDate: string, disabilityDate: string): number {
  if (!hireDate || !disabilityDate) {
    return 0;
  }

  const hireDateObj = parseUTCDate(hireDate);
  const disabilityDateObj = parseUTCDate(disabilityDate);

  if (isNaN(hireDateObj.getTime()) || isNaN(disabilityDateObj.getTime())) {
    return 0;
  }

  // Calculate longevity (Years of Service)
  const longevity =
    disabilityDateObj.getUTCFullYear() - hireDateObj.getUTCFullYear() + 1;

  return longevity;
}

// Excel DATEDIF equivalent function - calculates complete years between dates
function calculateDateDifYears(startDate: Date, endDate: Date): number {
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0;
  }

  let years = endDate.getUTCFullYear() - startDate.getUTCFullYear();
  
  // Check if we need to subtract a year (if end date hasn't reached anniversary)
  const startMonth = startDate.getUTCMonth();
  const startDay = startDate.getUTCDate();
  const endMonth = endDate.getUTCMonth();
  const endDay = endDate.getUTCDate();
  
  if (endMonth < startMonth || (endMonth === startMonth && endDay < startDay)) {
    years--;
  }
  
  return Math.max(0, years);
}

// Calculate longevity for specific month during disability period (Excel formula equivalent)
function calculateMonthlyLongevity(hireDate: string, monthDate: Date): number {
  if (!hireDate || !monthDate) {
    return 0;
  }

  const hireDateObj = parseUTCDate(hireDate);
  
  if (isNaN(hireDateObj.getTime()) || isNaN(monthDate.getTime())) {
    return 0;
  }

  // Excel formula: =IFERROR(DATEDIF($Q$17, W56, "y")+1,0)
  const yearsDiff = calculateDateDifYears(hireDateObj, monthDate);
  return yearsDiff + 1;
}

function getPermonthValueFromLookup(longevity: number, vacationLookup: VacationLookupEntry[]): number {
    // Find closest YOS entry in vacation lookup
    let perMonthValue = 0;
    if (vacationLookup && vacationLookup.length > 0) {
      const lookupEntry = vacationLookup.find(entry => entry.YOS === longevity);
      
      if (lookupEntry && lookupEntry.PerMonth) {
        perMonthValue = lookupEntry.PerMonth;
      } else {
        // If exact match not found, find closest YOS
        const sortedEntries = vacationLookup.sort((a, b) => Math.abs(a.YOS - longevity) - Math.abs(b.YOS - longevity));
        
        if (sortedEntries.length > 0 && sortedEntries[0].PerMonth) {
          perMonthValue = sortedEntries[0].PerMonth;
        }
      }
    }
    // Round to 5 decimal points
    return Math.round(perMonthValue * 100000) / 100000;
}

function getDaysFromVacationTable(longevity: number, vacationLookup: VacationLookupEntry[]): number {
    // Find closest YOS entry in vacation lookup and get Days value divided by 12
    let perMonthValue = 0;
    if (vacationLookup && vacationLookup.length > 0) {
      const lookupEntry = vacationLookup.find(entry => entry.YOS === longevity);
      
      if (lookupEntry && lookupEntry.Days) {
        perMonthValue = lookupEntry.Days / 12; // Divide Days by 12 to get monthly value
      } else {
        // If exact match not found, find closest YOS
        const sortedEntries = vacationLookup.sort((a, b) => Math.abs(a.YOS - longevity) - Math.abs(b.YOS - longevity));
        
        if (sortedEntries.length > 0 && sortedEntries[0].Days) {
          perMonthValue = sortedEntries[0].Days / 12; // Divide Days by 12 to get monthly value
        }
      }
    }
    // Return with 5 decimal places without rounding
    return Math.round(perMonthValue * 100000) / 100000;
}

/**
 * Calculate total vacation days from all January months between disability and retirement
 */
function calculateTotalJanuaryVacationDays(
  disabilityDate: Date,
  retirementDate: Date,
  employmentInfo: { hireDate?: string },
  vacationRates: VacationLookupEntry[]
): number {
  const hireDate = parseUTCDate(employmentInfo.hireDate);
  const vacationLookup = vacationRates;
  let totalDays = 0;

  // Start from the first January after disability
  let currentYear = disabilityDate.getUTCFullYear();
  if (disabilityDate.getUTCMonth() === 0) {
    // If disability is in January, include this January
    currentYear = disabilityDate.getUTCFullYear();
  } else {
    // Otherwise start from next January
    currentYear = disabilityDate.getUTCFullYear() + 1;
  }

  const retirementYear = retirementDate.getUTCFullYear();

  // Loop through each January between disability and retirement
  while (currentYear < retirementYear) {
    // Calculate YOS at this January (year-based calculation)
    const hireYear = hireDate.getUTCFullYear();
    const yearsOfService = currentYear - hireYear;

    // Find vacation days for this YOS
    const lookupEntry = vacationLookup.find(
      (entry) => entry.YOS === yearsOfService
    );
    let vacationDays;
    if (lookupEntry) {
      vacationDays = lookupEntry.Days;
    } else if (yearsOfService < 1) {
      vacationDays = 14; // Minimum for YOS < 1
    } else if (yearsOfService >= 19) {
      vacationDays = 35; // Max vacation days for YOS >= 19
    } else {
      vacationDays = 14; // Fallback to minimum
    }

    totalDays += vacationDays;

    currentYear++;
  }

  return totalDays;
}

function calculateMonthsToRetirement(
  dateOfBirth: string,
  disabilityDate: string
): number {
  if (!dateOfBirth || !disabilityDate) {
    return 0;
  }

  const dobUTC = parseUTCDate(dateOfBirth);
  const disabilityUTC = parseUTCDate(disabilityDate);

  if (isNaN(dobUTC.getTime()) || isNaN(disabilityUTC.getTime())) {
    return 0;
  }

  // Calculate 65th birthday using UTC
  const retirementDate = new Date(
    Date.UTC(
      dobUTC.getUTCFullYear() + 65,
      dobUTC.getUTCMonth(),
      dobUTC.getUTCDate()
    )
  );

  // Calculate months between disability date and retirement date
  const monthsDiff =
    (retirementDate.getUTCFullYear() - disabilityUTC.getUTCFullYear()) * 12 +
    (retirementDate.getUTCMonth() - disabilityUTC.getUTCMonth());

  return Math.max(0, monthsDiff + 1); // Ensure non-negative
}

/**
 * Calculate Assumption Values if missing from API
 */
function calculateAssumption(
  year: string,
  assumptions: Record<string, any>,
  target: string
) {
  const currentYearData = assumptions[year];
  const prevYearData = assumptions[String(toNumber(year) - 1)];

  if (!currentYearData && !prevYearData)
    throw new Error(`Cannot calculate assumption for year ${year} — missing data.`);

  let result = 0;

  switch (target) {
    case "SS_Wage":
      if (!prevYearData)
        throw new Error(`Previous year missing for SS_Wage calculation: ${year}`);
      result = toNumber(prevYearData["SS_Wage"]) + 3560;
      break;

    case "_415c":
      result = calculateRounded(toNumber(currentYearData["_401a17"]), 0.2, 100);
      break;

    case "_402g":
      result = calculateRounded(toNumber(currentYearData["_415c"]), 0.335, 1000);
      break;

    case "_414v":
      result = calculateRounded(toNumber(currentYearData["_415c"]), 0.105, 100);
      break;

    case "SUPER":
      result = calculateRounded(toNumber(currentYearData["_415c"]), 0.161, 10);
      break;

    default:
      throw new Error(`Unknown assumption target: ${target}`);
  }

  return result;
}
// Export additional functions that might be needed in other files
export {
  calculateInitialVacationBalance,
  calculateLOLTaxBenefit,
  calculateGreatestBase,
  getDynamicTFPRate,
  getDynamicTFPRateWithLongevity,
  calculateSTDWithYear,
  calculateYearlyTotals,
  calculateLongevity,
  calculateMonthlyLongevity,
  getPermonthValueFromLookup,
  getDaysFromVacationTable,
  calculateTotalJanuaryVacationDays,
  calculateMonthsToRetirement,
};
